import type { ColDef, IRowNode } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Table } from "@performyard/py-components";
import { ReviewDetailsFrontend } from "@performyard/service-contracts";

import { FilterFormSchema } from "./FilterPanel/types";

import { useReviewCycleBuilderPageContext } from "../hooks/ReviewCycleBuilderPageContext";

import { doesFilterPass, isValidValue } from "./FilterPanel/utils";

import { TABLE_HEADER_HEIGHT, TABLE_ROW_HEIGHT, getTableSizing } from "../table-height";
import { columnTypes } from "./columns";

const ReviewsDetailsTable = (props: {
	queryParams: FilterFormSchema;
	columnData: ColDef<ReviewDetailsFrontend>[];
	gridRef: React.RefObject<AgGridReact<ReviewDetailsFrontend> | null>;
}) => {
	const { queryParams, columnData, gridRef } = props;

	// Fetch data and add chunks to rowData
	const { reviewCycleReviewsDetails } = useReviewCycleBuilderPageContext();

	// Filter out empty filter values
	const activeFilters: Partial<FilterFormSchema> = useMemo(
		() =>
			// Filter out null, undefined, empty string, and empty array values into an object
			Object.fromEntries(Object.entries(queryParams).filter((entry) => isValidValue(entry[1]))),
		[queryParams]
	);

	/**
	 * Call the AG-Grid onFilterChanged when the external filter changes.
	 *
	 * This is used to trigger a re-render of the table data when the external filter changes.
	 * Used in conjunction with the isExternalFilterPresent and doesExternalFilterPass callbacks.
	 *
	 */
	useEffect(() => {
		gridRef.current?.api?.onFilterChanged();
	}, [activeFilters]);

	/**
	 * Callback used by AG-Grid to determine if it should apply the external filter.
	 *
	 * Return true if there an external filter is applied.
	 *
	 */
	const isExternalFilterPresent = () => {
		return Object.keys(activeFilters).length > 0;
	};

	/**
	 * Callback used by AG-Grid to evaulate the external filter against each row.
	 *
	 * When isExternalFilterPresent returns true, AG-Grid will call this callback for each row.
	 *
	 */
	const doesExternalFilterPass = (rowNode: IRowNode<ReviewDetailsFrontend>) => {
		return doesFilterPass(rowNode, activeFilters);
	};

	/**
	 * Rows are hidden by AG-Grid's external filter rather than by trimming rowData, so the count to
	 * size the viewport against has to come from the grid rather than from reviewCycleReviewsDetails.
	 * onModelUpdated covers both a data load and a filter change.
	 */
	const [displayedRowCount, setDisplayedRowCount] = useState(0);

	const handleModelUpdated = useCallback(() => {
		const api = gridRef.current?.api;
		if (!api || api.isDestroyed()) return;
		setDisplayedRowCount(api.getDisplayedRowCount());
	}, [gridRef]);

	const { autoHeight, height: tableHeight } = getTableSizing(displayedRowCount);

	return (
		<div className="w-full" style={tableHeight === undefined ? undefined : { height: tableHeight }}>
			<Table<ReviewDetailsFrontend>
				ref={gridRef}
				autoHeight={autoHeight}
				rowData={reviewCycleReviewsDetails}
				columnDefs={columnData}
				columnTypes={columnTypes}
				rowHeight={TABLE_ROW_HEIGHT}
				headerHeight={TABLE_HEADER_HEIGHT}
				onModelUpdated={handleModelUpdated}
				suppressScrollOnNewData={true} // Don't scroll when data is changed
				isExternalFilterPresent={isExternalFilterPresent}
				doesExternalFilterPass={doesExternalFilterPass}
				suppressCellFocus={true} // Don't highlight cells when clicked
				suppressRowHoverHighlight={true} // Don't highlight rows when hovered
			/>
		</div>
	);
};

export default ReviewsDetailsTable;
